"use client";
import { Room } from "livekit-client";
import { useRef, useCallback } from "react";
import { useLeaveHandler } from "./useLeaveHandler";
import { toastNotify } from "@client/components/ui/toast";
import { HostLeaveOption } from "@shared/constants/constant";
import { useTranslations } from "next-intl";
import {
  muteParticipant,
  unmuteParticipant,
  kickParticipant,
  muteAllParticipants,
  applyHostBandwidthControls
} from "@client/actions/participant/participant.actions";
import { Logger } from "@shared/utils/logger";

export interface UseMeetHandlersOptions {
  room: Room;
  /** Meeting link (DB room.link) for moderator API authorization */
  meetingLink?: string;
  isRecording?: boolean;
  onStopRecording?: () => Promise<void>;
  onBeforeMeetingEnd?: () => Promise<void>;
  onIntentionalLeave?: () => void;
}

interface UseMeetHandlersReturn {
  handleLeave: () => Promise<void>;
  handleHostLeave: (option: HostLeaveOption) => Promise<void>;
  showLeaveModal: boolean;
  openLeaveModal: () => void;
  closeLeaveModal: () => void;
  isLeaving: boolean;
  loadingOption: HostLeaveOption | null;
  participantCount: number;
  handleMuteParticipant: (participantIdentity: string) => Promise<void>;
  handleUnmuteParticipant: (participantIdentity: string) => Promise<void>;
  handleMuteParticipantVideo: (participantIdentity: string) => Promise<void>;
  handleUnmuteParticipantVideo: (participantIdentity: string) => Promise<void>;
  handleKickParticipant: (participantIdentity: string, userId?: number, guestId?: string) => Promise<void>;
  handleMuteAllParticipants: () => Promise<void>;
  handleStopAllParticipantVideos: () => Promise<void>;
  handleStopAllScreenShares: () => Promise<void>;
}

/**
 * Consolidates all meeting action handlers (leave, mute, kick).
 * Optimized with stable callbacks to prevent unnecessary re-renders.
 */
export const useMeetHandlers = ({
  room,
  meetingLink,
  isRecording = false,
  onStopRecording,
  onBeforeMeetingEnd,
  onIntentionalLeave
}: UseMeetHandlersOptions): UseMeetHandlersReturn => {
  const t = useTranslations();
  const apiRoomNameRef = useRef(meetingLink ?? room.name);
  apiRoomNameRef.current = meetingLink ?? room.name;

  // Leave handler with host modal support
  const {
    handleLeave,
    handleHostLeave,
    showLeaveModal,
    openLeaveModal,
    closeLeaveModal,
    isLeaving,
    loadingOption,
    participantCount
  } = useLeaveHandler({
    room,
    isRecording,
    onStopRecording,
    onBeforeMeetingEnd,
    onIntentionalLeave
  });

  const handleMuteParticipant = useCallback(
    async (participantIdentity: string) => {
      try {
        const result = await muteParticipant({
          roomName: apiRoomNameRef.current,
          participantIdentity,
          trackType: "audio"
        });

        if (!result.status) {
          throw new Error(result.message || "Failed to mute participant");
        }

        toastNotify.success(t("toast.participant.muted"));
      } catch (error) {
        Logger.error("useMeetHandlers", "Error muting participant:", error);
        toastNotify.error(
          `${t("toast.participant.muteFailed")}: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    },
    [t]
  );

  /**
   * Request a participant to unmute their audio track.
   * Note: LiveKit doesn't allow remote unmute for privacy - this sends a request.
   */
  const handleUnmuteParticipant = useCallback(
    async (participantIdentity: string) => {
      try {
        const result = await unmuteParticipant({
          roomName: apiRoomNameRef.current,
          participantIdentity,
          trackType: "audio"
        });

        if (!result.status) {
          throw new Error(result.message || "Failed to send unmute request");
        }

        toastNotify.success(t("toast.participant.unmuteRequestSent"));
      } catch (error) {
        Logger.error("useMeetHandlers", "Error sending unmute request:", error);
        toastNotify.error(
          `${t("toast.participant.unmuteRequestFailed")}: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    },
    [t]
  );

  /**
   * Mute a participant's video track.
   */
  const handleMuteParticipantVideo = useCallback(
    async (participantIdentity: string) => {
      try {
        const result = await muteParticipant({
          roomName: apiRoomNameRef.current,
          participantIdentity,
          trackType: "video"
        });

        if (!result.status) {
          throw new Error(result.message || "Failed to mute participant video");
        }

        toastNotify.success(t("toast.participant.videoDisabled"));
      } catch (error) {
        Logger.error("useMeetHandlers", "Error muting participant video:", error);
        toastNotify.error(
          `${t("toast.participant.videoDisableFailed")}: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    },
    [t]
  );

  /**
   * Request a participant to enable their video.
   * Note: LiveKit doesn't allow remote unmute for privacy - this sends a request.
   */
  const handleUnmuteParticipantVideo = useCallback(
    async (participantIdentity: string) => {
      try {
        const result = await unmuteParticipant({
          roomName: apiRoomNameRef.current,
          participantIdentity,
          trackType: "video"
        });

        if (!result.status) {
          throw new Error(result.message || "Failed to send video enable request");
        }

        toastNotify.success(t("toast.participant.videoEnableRequestSent"));
      } catch (error) {
        Logger.error("useMeetHandlers", "Error sending video enable request:", error);
        toastNotify.error(
          `${t("toast.participant.videoEnableRequestFailed")}: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    },
    [t]
  );

  /**
   * Kick (remove) a participant from the room.
   */
  const handleKickParticipant = useCallback(
    async (participantIdentity: string, userId?: number, guestId?: string) => {
      try {
        const result = await kickParticipant({
          roomName: apiRoomNameRef.current,
          participantIdentity,
          userId,
          guestId
        });

        if (!result.status) {
          throw new Error(result.message || "Failed to kick participant");
        }

        toastNotify.success(t("toast.participant.removed"));
      } catch (error) {
        Logger.error("useMeetHandlers", "Error kicking participant:", error);
        toastNotify.error(
          `${t("toast.participant.kickFailed")}: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    },
    [t]
  );

  /**
   * Mute all participants' audio (host only).
   * Calls the mute-all server action and shows a summary toast.
   */
  const handleMuteAllParticipants = useCallback(async () => {
    try {
      const result = await muteAllParticipants({
        roomName: apiRoomNameRef.current
      });

      if (!result.status) {
        throw new Error(result.message || "Failed to mute all participants");
      }

      toastNotify.success(t("toast.participant.allMuted", { count: result.data?.mutedCount ?? 0 }));
    } catch (error) {
      Logger.error("useMeetHandlers", "Error muting all participants:", error);
      toastNotify.error(
        `${t("toast.participant.allMuteFailed")}: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }, [t]);

  const handleStopAllParticipantVideos = useCallback(async () => {
    try {
      const result = await applyHostBandwidthControls({
        roomName: apiRoomNameRef.current,
        action: "stop-all-videos"
      });
      if (!result.status) {
        throw new Error(result.message || "Failed to turn off participant videos");
      }
      toastNotify.success(t("toast.participant.allVideosStopped", { count: result.data?.affectedCount ?? 0 }));
    } catch (error) {
      Logger.error("useMeetHandlers", "Error stopping all participant videos:", error);
      toastNotify.error(
        `${t("toast.participant.allVideosStopFailed")}: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }, [t]);

  const handleStopAllScreenShares = useCallback(async () => {
    try {
      const result = await applyHostBandwidthControls({
        roomName: apiRoomNameRef.current,
        action: "stop-all-screen-shares"
      });
      if (!result.status) {
        throw new Error(result.message || "Failed to stop screen shares");
      }
      toastNotify.success(t("toast.participant.allScreenSharesStopped", { count: result.data?.affectedCount ?? 0 }));
    } catch (error) {
      Logger.error("useMeetHandlers", "Error stopping all screen shares:", error);
      toastNotify.error(
        `${t("toast.participant.allScreenSharesStopFailed")}: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }, [t]);

  return {
    handleLeave,
    handleHostLeave,
    showLeaveModal,
    openLeaveModal,
    closeLeaveModal,
    isLeaving,
    loadingOption,
    participantCount,
    handleMuteParticipant,
    handleUnmuteParticipant,
    handleMuteParticipantVideo,
    handleUnmuteParticipantVideo,
    handleKickParticipant,
    handleMuteAllParticipants,
    handleStopAllParticipantVideos,
    handleStopAllScreenShares
  };
};
